import {
  ChannelGroupListDTO,
  ChannelGroupStatusFilter
} from 'core/channelGroup/ChannelGroup';
import { useCallback, useMemo, useState } from 'react';
import {
  getNameIdColumn,
  getDefaultColumnDescription,
  getCustomRenderColumn,
  ColumnDefinition
} from 'components/TableColumn/TableColumn';
import i18n from 'i18n';
import { FilterMenuTabConfig } from 'components/common/FilterMenuTab/FilterMenuTab';
import { ArchiveReasonCell } from './ArchiveReasonCell';

export enum ChannelGroupListColumns {
  ID = 'id',
  STATUS = 'effectiveArchived',
  ARCHIVE_REASON = 'archiveReason',
  PUBLISHER_ID = 'publisherIds'
}

export type ChannelGroupListData = {
  readonly searchString: string;
  readonly filteredList: ChannelGroupListDTO[];
  readonly columns: ColumnDefinition[];
  readonly filterMenuTabConfigs: FilterMenuTabConfig[];
  onSearch(searchString: string): void;
};

/**
 * ECOPD-5701：狀態篩選的三個選項。順序就是選單上的顯示順序。
 */
const STATUS_FILTER_ORDER = [
  ChannelGroupStatusFilter.ALL,
  ChannelGroupStatusFilter.ACTIVE,
  ChannelGroupStatusFilter.ARCHIVED
];

const statusFilterLabels = (): Record<ChannelGroupStatusFilter, string> => ({
  [ChannelGroupStatusFilter.ALL]: i18n.t('channelGroup.status.all'),
  [ChannelGroupStatusFilter.ACTIVE]: i18n.t('channelGroup.status.active'),
  [ChannelGroupStatusFilter.ARCHIVED]: i18n.t('channelGroup.status.archived')
});

export const useChannelGroupListModel = (
  channelGroups: ChannelGroupListDTO[],
  statusFilter: ChannelGroupStatusFilter,
  onStatusFilterChange: (status: ChannelGroupStatusFilter) => void
): ChannelGroupListData => {
  const [searchString, onSearch] = useState<string>('');

  // ECOPD-5701：ECOPD-5566 的「(停用)」後綴在這張列表拿掉了，狀態改由獨立欄位承擔。
  //
  // ⚠️ 只拿掉這一處。`withSalesChannelArchivedSuffix` 另外三個使用處（全站 i18n 名稱、
  // 報表渠道選項、`ReportWebService`）都要留著 —— 那些畫面沒有狀態欄。
  // 副作用（刻意接受）：不能再靠搜「停用」二字找停用渠道，改用狀態篩選器。
  const filteredList = useMemo(
    () =>
      channelGroups.filter(
        (channelGroup) =>
          channelGroup.id.toString().includes(searchString) ||
          (channelGroup.name ?? '').includes(searchString)
      ),
    [searchString, channelGroups]
  );

  const columns = useMemo(
    () => [
      getNameIdColumn(
        getDefaultColumnDescription(
          ChannelGroupListColumns.ID,
          i18n.t('channelGroup.labels.name')
        ),
        (channelGroup) => `/system/channel-groups/${channelGroup.id}`
      ),
      // 一律讀後端算好的 `effectiveArchived`，**不要**自己用 `status` 與
      // `pmaxArchived` 重算 —— 定義漂移的症狀是各頁顯示不一致，而且不會有測試紅。
      // 來源（誰停的）不進狀態欄，由停用理由的前綴承載。
      {
        ...getDefaultColumnDescription(
          ChannelGroupListColumns.STATUS,
          i18n.t('channelGroupList.headers.status')
        ),
        formatter: (effectiveArchived) =>
          effectiveArchived
            ? i18n.t('channelGroup.status.archived')
            : i18n.t('channelGroup.status.active')
      },
      getCustomRenderColumn(
        getDefaultColumnDescription(
          ChannelGroupListColumns.ARCHIVE_REASON,
          i18n.t('channelGroupList.headers.archiveReason'),
          // 不可排序：自由文字排序沒意義，而且它由兩個來源組出來、
          // 不是 DTO 上的單一欄位，排序會落在不存在的 dataField 上。
          false
        ),
        (channelGroup: ChannelGroupListDTO) => (
          <ArchiveReasonCell channelGroup={channelGroup} />
        )
      ),
      {
        ...getDefaultColumnDescription(
          ChannelGroupListColumns.PUBLISHER_ID,
          i18n.t('channelGroup.labels.publisherIds')
        ),
        formatter: (value) => (value ?? []).join(', ')
      }
    ],
    []
  );

  // 三態單選。`selectionMode: 'single'` 只保證「最多一個」，**不保證非空** ——
  // 點 tag 上的 × 會送出空陣列，所以要自己 fallback。與 `AgenciesHomeModel` 一致。
  const handleOnStatusFilterApply = useCallback(
    (values: string[]) => {
      const labels = statusFilterLabels();
      if (values.length === 0) {
        onStatusFilterChange(ChannelGroupStatusFilter.ALL);
        return;
      }
      const chosenLabel = values[values.length - 1];
      const matched = STATUS_FILTER_ORDER.find(
        (status) => labels[status] === chosenLabel
      );
      onStatusFilterChange(matched ?? ChannelGroupStatusFilter.ALL);
    },
    [onStatusFilterChange]
  );

  const filterMenuTabConfigs = useMemo(() => {
    const labels = statusFilterLabels();
    return [
      {
        filterType: i18n.t('channelGroup.filter.statusFilter'),
        menuTitle: i18n.t('channelGroup.filter.statusFilterMenuTitle'),
        tag: i18n.t('channelGroup.filter.statusTag'),
        // 恆長度 1 → 畫面永遠只有一個 tag。
        selectedValues: [labels[statusFilter]],
        options: STATUS_FILTER_ORDER.map((status) => labels[status]),
        applyMethod: handleOnStatusFilterApply,
        selectionMode: 'single' as const
      }
    ];
  }, [statusFilter, handleOnStatusFilterApply]);

  return {
    searchString,
    filteredList,
    columns,
    filterMenuTabConfigs,
    onSearch
  };
};
